import React, { useState } from "react";
import { SkipToken, skipToken } from "@reduxjs/toolkit/query";
import { produce } from "immer";
import { CommonItem, GenericSort } from "@sscs/types";
import { Autocomplete, FilterBar, Menu, SearchBar, Select } from "@sscs/ui";
import { ORGANIZATION_CATALOG_ID, PERMISSION } from "Constants";
import { useAllowedTo } from "Hooks";
import useCatalogParams from "../../hooks/useCatalogParams";
import {
  useGetExternalCatalogCategoriesQuery,
  useGetExternalCatalogLicensesQuery,
  useGetExternalCatalogPublishersQuery,
  useGetSoftwareCatalogCategoriesQuery,
  useGetSoftwareCatalogLicensesQuery,
  useGetSoftwareCatalogPublishersQuery,
} from "../../services/softwareCatalog.api";
import {
  ExternalFilterOptionsRequest,
  FilterOptionsRequest,
  SoftwareCatalogFilter,
} from "../../types";

const ALL_OPTIONS = "ALL";

const getAutocompleteOptions = (
  options: CommonItem[] | undefined,
  name: string,
): CommonItem[] => {
  return [
    {
      id: ALL_OPTIONS,
      value: `Show all ${name}`,
    },
  ].concat(options ?? []);
};

type OptionsFilter = "category" | "publisher" | "license";

type FilterAndSortProps<SortKey extends string> = {
  filter: SoftwareCatalogFilter;
  sort: GenericSort<SortKey>;
  sortOptions: ReadonlyArray<{ property: SortKey; label: string }>;
  onFilterChange: (newFilter: SoftwareCatalogFilter) => void;
  onSortChange: (newSort: GenericSort<SortKey>) => void;
};

const FilterAndSort = <SortKey extends string>({
  filter,
  sort,
  sortOptions,
  onFilterChange,
  onSortChange,
}: FilterAndSortProps<SortKey>): React.JSX.Element => {
  const { groupId, catalogId, platform, architecture, extension } =
    useCatalogParams();
  const isOrganizationCatalog = catalogId === ORGANIZATION_CATALOG_ID;
  const canSeeCatalogPresence = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });
  const [openFilter, setOpenFilter] = useState<OptionsFilter | null>(null);

  const shouldLoadOptions = (name: OptionsFilter): boolean =>
    openFilter === name || Boolean(filter[name]);
  const organizationRequest =
    isOrganizationCatalog && groupId && platform
      ? { groupId, platform, architecture, extension }
      : null;
  const externalRequest =
    !isOrganizationCatalog && catalogId && groupId && platform
      ? {
          catalog: catalogId,
          groupId,
          platform,
          architecture,
          extension,
        }
      : null;
  const organizationArgs = (
    name: OptionsFilter,
  ): FilterOptionsRequest | SkipToken =>
    organizationRequest && shouldLoadOptions(name)
      ? organizationRequest
      : skipToken;
  const externalArgs = (
    name: OptionsFilter,
  ): ExternalFilterOptionsRequest | SkipToken =>
    externalRequest && shouldLoadOptions(name) ? externalRequest : skipToken;

  const organizationCategories = useGetSoftwareCatalogCategoriesQuery(
    organizationArgs("category"),
  );
  const organizationLicenses = useGetSoftwareCatalogLicensesQuery(
    organizationArgs("license"),
  );
  const organizationPublishers = useGetSoftwareCatalogPublishersQuery(
    organizationArgs("publisher"),
  );
  const externalCategories = useGetExternalCatalogCategoriesQuery(
    externalArgs("category"),
  );
  const externalLicenses = useGetExternalCatalogLicensesQuery(
    externalArgs("license"),
  );
  const externalPublishers = useGetExternalCatalogPublishersQuery(
    externalArgs("publisher"),
  );

  const categories = isOrganizationCatalog
    ? organizationCategories
    : externalCategories;
  const licenses = isOrganizationCatalog
    ? organizationLicenses
    : externalLicenses;
  const publishers = isOrganizationCatalog
    ? organizationPublishers
    : externalPublishers;

  const categoryOptions = getAutocompleteOptions(categories.data, "categories");
  const licenseOptions = getAutocompleteOptions(licenses.data, "licenses");
  const publisherOptions = getAutocompleteOptions(
    publishers.data,
    "publishers",
  );

  return (
    <FilterBar
      filters={[
        {
          id: "name",
          row: 1,
          control: (
            <SearchBar
              id="software-name-filter"
              label="Search by software name"
              value={filter.name ?? ""}
              onChange={(value) =>
                onFilterChange(
                  produce(filter, (draft) => {
                    draft.name = value === "" ? null : value;
                  }),
                )
              }
            />
          ),
        },
        {
          id: "category",
          row: 2,
          control: (
            <Autocomplete
              id="software-category-filter"
              label={`Filter by category`}
              isClearAlwaysVisible={filter.category !== null}
              value={filter.category ?? ALL_OPTIONS}
              onOpen={() => setOpenFilter("category")}
              onClose={() => setOpenFilter(null)}
              loading={categories.isLoading || categories.isFetching}
              options={categoryOptions.map((option): string => option.id)}
              getOptionLabel={(option) =>
                categoryOptions.find((item) => item.id === option)?.value ?? ""
              }
              onChange={(_event, value) =>
                onFilterChange(
                  produce(filter, (draft) => {
                    draft.category = value === ALL_OPTIONS ? null : value;
                  }),
                )
              }
            />
          ),
        },
        {
          id: "publisher",
          row: 2,
          control: (
            <Autocomplete
              id="software-publisher-filter"
              label={`Filter by publisher`}
              isClearAlwaysVisible={filter.publisher !== null}
              value={filter.publisher ?? ALL_OPTIONS}
              onOpen={() => setOpenFilter("publisher")}
              onClose={() => setOpenFilter(null)}
              loading={publishers.isLoading || publishers.isFetching}
              options={publisherOptions.map((option): string => option.id)}
              getOptionLabel={(option) =>
                publisherOptions.find((item) => item.id === option)?.value ?? ""
              }
              onChange={(_event, value) =>
                onFilterChange(
                  produce(filter, (draft) => {
                    draft.publisher = value === ALL_OPTIONS ? null : value;
                  }),
                )
              }
            />
          ),
        },
        {
          id: "license",
          row: 2,
          control: (
            <Autocomplete
              id="software-licenses-filter"
              label={`Filter by license`}
              isClearAlwaysVisible={filter.license !== null}
              value={filter.license ?? ALL_OPTIONS}
              onOpen={() => setOpenFilter("license")}
              onClose={() => setOpenFilter(null)}
              loading={licenses.isLoading || licenses.isFetching}
              options={licenseOptions.map((option): string => option.id)}
              getOptionLabel={(option) =>
                licenseOptions.find((item) => item.id === option)?.value ?? ""
              }
              onChange={(_event, value) =>
                onFilterChange(
                  produce(filter, (draft) => {
                    draft.license = value === ALL_OPTIONS ? null : value;
                  }),
                )
              }
            />
          ),
        },
        {
          id: "in-catalog",
          row: 2,
          width: "190px",
          hidden: isOrganizationCatalog && !canSeeCatalogPresence,
          control: (
            <Select
              displayEmpty
              shrinkLabel
              id="in-catalog-filter"
              labelId="in-catalog-filter-label"
              label={
                isOrganizationCatalog
                  ? "Filter by catalog presence"
                  : "Filter by import state"
              }
              value={filter.in_catalog?.toString() ?? ALL_OPTIONS}
              onChange={(value) =>
                onFilterChange(
                  produce(filter, (draft) => {
                    if (value === ALL_OPTIONS) {
                      draft.in_catalog = null;
                    } else {
                      draft.in_catalog = value === "true";
                    }
                  }),
                )
              }
            >
              <Menu.Item value={ALL_OPTIONS}>Show all software</Menu.Item>
              <Menu.Item value={true.toString()}>
                {isOrganizationCatalog ? "In catalog" : "Imported"}
              </Menu.Item>
              <Menu.Item value={false.toString()}>
                {isOrganizationCatalog ? "Not in catalog" : "Not imported"}
              </Menu.Item>
            </Select>
          ),
        },
      ]}
      sort={{
        id: "software-sort",
        options: sortOptions,
        value: sort,
        onChange: onSortChange,
      }}
    />
  );
};

export default FilterAndSort;
